import { useEffect, useState } from "react";
import { Play, Pause, Loader2, Tv, Radio } from "lucide-react";
import { useApi } from "../../lib/api";
import { useRadio } from "../../context/RadioContext";

const Chips = ({ list, sel, onPick }) => (
  <div className="flex flex-wrap gap-2" data-testid="stage-station-chips">
    {list.map((s) => (
      <button key={s.key} onClick={() => onPick(s)} className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-colors ${sel.key === s.key ? "border-[color:var(--gold-hi)] bg-[#0e0b04] text-[#f7e08f]" : "border-[color:var(--line)] bg-white text-ink hover:border-[color:var(--gold-hi)]"}`} data-testid={`stage-chip-${s.key}`}>
        <img src={s.logo} alt="" className="w-7 h-7 rounded-full bg-white object-cover" />
        <span className="text-xs font-semibold">{s.name}</span>
        {s.type === "tv" ? <Tv size={12} /> : <Radio size={12} />}
      </button>
    ))}
  </div>
);

const NowLine = ({ now }) => (
  <div className="text-sm text-white/80" data-testid="stage-now">
    <span className="font-data text-[10px] uppercase tracking-widest text-[#f7e08f] mr-2">On air</span>
    {now ? <>{now.title} · <span className="text-white/60">{now.host} · {now.start}–{now.end}</span></> : "Music & continuity"}
  </div>
);

export const StationStage = () => {
  const { data } = useApi("/public/whats-on");
  const radio = useRadio();
  const list = data?.stations || [];
  const [pick, setPick] = useState(null);
  useEffect(() => { setPick(null); }, [radio.station?.key]);
  if (!list.length) return <div className="h-64 panel animate-pulse" />;
  const sel = pick || list.find((s) => s.key === radio.station?.key) || list[0];
  const mine = radio.station?.key === sel.key;
  const state = mine ? radio.state : "idle";
  const onPick = (s) => {
    setPick(s);
    if (s.type === "tv") return (radio.state === "playing" || radio.state === "loading") && radio.stop();
    if (radio.state === "playing" || radio.state === "loading") radio.play(s);
  };
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Play;

  return (
    <div className="space-y-4" data-testid="station-stage">
      <Chips list={list} sel={sel} onPick={onPick} />
      <div className="rounded-lg bg-[#0e0b04] gold-frame-lg shadow-2xl overflow-hidden" data-testid={`stage-screen-${sel.key}`}>
        {sel.type === "tv" ? (
          <div className="aspect-video bg-black"><iframe key={sel.key} title={`${sel.name} live`} src={sel.tv_embed} className="w-full h-full border-0" allow="autoplay; fullscreen; encrypted-media" allowFullScreen data-testid="stage-tv-iframe" /></div>
        ) : (
          <div className="p-5 md:p-7 flex items-center gap-5">
            <img src={sel.logo} alt={`${sel.name} logo`} className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white object-cover ring-4 ring-[color:var(--gold-hi)] shrink-0" data-testid="stage-logo" />
            <div className="min-w-0 flex-1">
              <div className="font-display text-4xl md:text-5xl font-extrabold gold-text leading-none" data-testid="stage-freq">{sel.freq}<span className="text-xl ml-1">FM</span></div>
              <div className="text-white font-semibold mt-2">{sel.name} · {sel.city}</div>
              <button onClick={() => (state === "playing" || state === "loading" ? radio.stop() : radio.play(sel))} className="mt-4 inline-flex items-center gap-2 rounded-full gold-foil pl-2 pr-5 py-2 text-[#1b1305] font-bold text-sm hover:scale-105 transition-transform" data-testid="stage-play-btn">
                <span className="w-8 h-8 rounded-full bg-[#1b1305]/10 flex items-center justify-center"><Icon size={16} className={state === "loading" ? "animate-spin" : ""} /></span>
                {state === "loading" ? "Tuning in…" : state === "playing" ? "Pause live" : "Listen live"}
              </button>
              {state === "error" && <p className="text-xs text-red-300 mt-2" data-testid="stage-error">{sel.name}'s official stream is offline at source right now.</p>}
            </div>
          </div>
        )}
        <div className="px-5 md:px-7 py-3 border-t border-white/10"><NowLine now={sel.now} /></div>
      </div>
    </div>
  );
};
